import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProfessionalContacts, type PropertyContactsSummary } from '@/client';
import {
	listPropertyContactsHousesHouseIdContactsGetOptions,
	listPropertyContactsHousesHouseIdContactsGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import { useSharingMutations } from '@/hooks/sharing/useSharingMutations';
import { usePropertyContacts } from '@/hooks/service-providers/usePropertyContacts';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { toaster } from '@/components/ui/toaster';
import { Roles } from '@/utils/roleUtils';
import { findContactByEmail } from '@/utils/access/sharingAccessUtils';
import { type AccessGrantFlow, type AccessGrantInput, sendInvitationAfterGrant } from '@/hooks/sharing/accessGrantFlow';

interface NewContactCard {
	email: string;
	name: string;
	categoryCode: string;
}

/**
 * Adds somebody to the property, files them as a contact, and tells them: the Access page's
 * meaning of the invite dialog.
 *
 * Add, then card, then invitation — the order `useConnectFlow` uses. A failure after the add
 * leaves somebody with access and no card, which "Save to services" on the Access drawer repairs,
 * rather than a card for somebody without access. The invitation is last because it is the one
 * that can be sent again.
 *
 * The card is filed only by a reader who may manage contacts, and only for an address no card
 * holds yet. The API links the card to the account whichever of the add and the card lands first.
 */
export const useInviteFlow = (houseId: number): AccessGrantFlow => {
	const queryClient = useQueryClient();
	const { addUser, inviteUser } = useSharingMutations(houseId);
	// Compared rather than passed through: usePropertyContacts defaults `enabled` to true, so an
	// absent flag would ask for contacts this reader may not read.
	const canFileContacts = useHousePermissions().canManageNetwork === true;
	const { data: contactsData } = usePropertyContacts(houseId, canFileContacts);

	const createContact = useMutation({
		mutationFn: (card: NewContactCard) =>
			ProfessionalContacts.addContactToPropertyHousesHouseIdContactsPost({
				path: { house_id: houseId },
				// A name always, so the backend files a person: a company-only body is saved as a company.
				body: { name: card.name, email: card.email, category_code: card.categoryCode },
				throwOnError: true,
			}),
	});

	const asksContactRole = (email: string) => canFileContacts && !findContactByEmail(contactsData, email);

	/** Resolves false only when the card did not land. A 409 is another tab filing the same card first. */
	const fileContact = async (card: NewContactCard): Promise<boolean> => {
		try {
			await createContact.mutateAsync(card);
			return true;
		} catch (error) {
			if (isAxiosError(error) && error.response?.status === 409) return true;
			console.error('Contact card failed after add:', error);
			return false;
		}
	};

	const submit = async (input: AccessGrantInput): Promise<boolean> => {
		const email = input.email.trim();
		const name = input.name?.trim() || undefined;

		const filesCard = !!input.contactRole && canFileContacts;
		// Opened before the list had loaded, the dialog asked for a role without knowing whether a card
		// already holds this address, so the list is read for the press itself. Started beside the add
		// rather than after it, and a failure is only the card's: null, never the grant's.
		const contactsLookup: Promise<PropertyContactsSummary | null> = filesCard
			? contactsData
				? Promise.resolve(contactsData)
				: queryClient
						.ensureQueryData(
							listPropertyContactsHousesHouseIdContactsGetOptions({ path: { house_id: houseId } }),
						)
						.catch((error: unknown) => {
							console.error('Contacts lookup failed before filing a card:', error);
							return null;
						})
			: Promise.resolve(null);

		try {
			const addedUser = await addUser.mutateAsync({
				path: { house_id: houseId },
				body: {
					email,
					role: input.role || Roles.Viewer,
					name,
					access_purpose: input.purpose?.trim() || undefined,
					invite_message: input.message?.trim() || undefined,
					expires_at: input.expiresAt?.toISOString() ?? null,
				},
			});

			if (filesCard && input.contactRole) {
				const contactsNow = await contactsLookup;
				const filed =
					contactsNow === null
						? false
						: findContactByEmail(contactsNow, email)
							? true
							: await fileContact({ email, name: name ?? email, categoryCode: input.contactRole });
				if (!filed) {
					toaster.create({
						title: 'Invited, but not saved to your contacts',
						description: 'Save them to services from the Access page.',
						type: 'error',
					});
				}
			}
			// The add links an existing card to the account, and a filed card is new: either way the
			// list has changed.
			void queryClient.invalidateQueries({
				queryKey: listPropertyContactsHousesHouseIdContactsGetQueryKey({ path: { house_id: houseId } }),
				refetchType: 'all',
			});

			await sendInvitationAfterGrant(
				inviteUser,
				{ house_id: houseId, user_id: addedUser.user_id },
				'Access granted',
			);

			return true;
		} catch (error) {
			console.error('Error adding user:', error);
			return false;
		}
	};

	return {
		submit,
		isPending: addUser.isPending || createContact.isPending || inviteUser.isPending,
		asksContactRole,
	};
};
